class QuickView extends HTMLElement {
  constructor() {
    super();
    this.listenerAttached = false;
    this.viewInstanceCounter = 0;
    this.initialize();
  }

  initialize() {
    if (!this.listenerAttached) {
      document.addEventListener("click", (e) => this.onDocumentClick(e));
      this.listenerAttached = true;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.matches(".js-open-quickview")) {
            node.addEventListener("click", (e) => {
              e.preventDefault();
              const url = node.dataset.url;
              if (url) this.loadQuickViewContent(url);
            });
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  async loadQuickViewContent(url) {
    try {
      const quickViewContainer = document.querySelector("quick-view");
      if (quickViewContainer) quickViewContainer.innerHTML = "";

      const response = await fetch(url);
      if (!response.ok) throw new Error("Network error");
      const responseText = await response.text();
      const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
      const productContent = parsedHTML.querySelector(".js-product");
      const sizeGuideContent = parsedHTML.querySelector("product-size-guide");

      if (!productContent) throw new Error("Product content not found");

      await this.loadRequiredAssets();

      const productId = productContent.getAttribute("data-product-id");
      this.injectProductJson(parsedHTML, `ProductJson${productId}`);
      this.injectProductJson(parsedHTML, `ProductJsonSizeGuide${productId}`);

      this.viewInstanceCounter++;
      const dynamicComponentName = `product-page-${this.viewInstanceCounter}`;
      const dynamicComponentNameSizeGuide = `product-size-guide`;

      const productWrapper = document.createElement(dynamicComponentName);
      if (productContent) productWrapper.appendChild(productContent);
      if (quickViewContainer) quickViewContainer.appendChild(productWrapper);
      if (sizeGuideContent) quickViewContainer.appendChild(sizeGuideContent);

      this.activateQuickViewDisplay();

      if (!customElements.get(dynamicComponentName)) {
        const ProductClass = class extends Product {};
        Object.defineProperty(ProductClass, 'name', { value: dynamicComponentName });
        customElements.define(dynamicComponentName, ProductClass);
      }

      if (!customElements.get(dynamicComponentNameSizeGuide)) {
        const ProductClassSizeGuide = class extends Product {};
        Object.defineProperty(ProductClassSizeGuide, 'name', { value: dynamicComponentNameSizeGuide });
        customElements.define(dynamicComponentNameSizeGuide, ProductClassSizeGuide);
      }

      if (typeof initializeObservers === "function") initializeObservers();

      document.querySelector('.js-drawer-cart')?.classList.add('closing');
      setTimeout(() => {
        document.querySelector('.js-drawer-cart')?.classList.remove('active', 'closing', 'display');
      }, 300);
    } catch (error) {
      console.error("QuickView load error:", error);
    } finally {
      setTimeout(() => this.setLoadingIndicator(false, this.lastClickedButton), 200);
    }
  }

  injectProductJson(parsedHTML, scriptId) {
    const scriptElement = parsedHTML.getElementById(scriptId);
    if (scriptElement && !document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.type = "application/json";
      script.id = scriptId;
      script.textContent = scriptElement.innerHTML;
      document.body.appendChild(script);
    }
  }

  loadRequiredAssets() {
    const assetPromises = [];

    quickViewCSS.forEach((url) => {
      if (!document.querySelector(`link[href='${url}']`)) {
        const linkTag = document.createElement("link");
        linkTag.rel = "stylesheet";
        linkTag.href = url;
        document.body.appendChild(linkTag);
        assetPromises.push(new Promise((res) => (linkTag.onload = res)));
      }
    });

    quickViewScripts.forEach((url) => {
      if (!document.querySelector(`script[src='${url}']`)) {
        const scriptTag = document.createElement("script");
        scriptTag.src = url;
        scriptTag.defer = true;
        document.body.appendChild(scriptTag);
        assetPromises.push(new Promise((res) => (scriptTag.onload = res)));
      }
    });

    return Promise.all(assetPromises);
  }

  activateQuickViewDisplay() {
    const viewElement = document.querySelector(".js-quickview");
    const backdropElement = document.querySelector(".js-quickview-background-close");

    [viewElement, backdropElement].forEach((el) => {
      if (el) {
        el.classList.add("display");
        setTimeout(() => el.classList.add("active"), 50);
      }
    });
  }

  closeQuickView() {
    const viewElement = document.querySelector(".js-quickview");
    const backdropElement = document.querySelector(".js-quickview-background-close");

    [viewElement, backdropElement].forEach((el) => el?.classList.add("closing"));

    setTimeout(() => {
      [viewElement, backdropElement].forEach((el) =>
        el?.classList.remove("active", "closing", "display")
      );
    }, 300);
  }

  onDocumentClick(event) {
    const openButton = event.target.closest(".js-open-quickview");
    if (openButton) {
      event.preventDefault();
      this.lastClickedButton = openButton;
      this.setLoadingIndicator(true, openButton);
      return this.loadQuickViewContent(openButton.dataset.url);
    }

    const closeButton = event.target.closest(".js-close-quickview");
    if (closeButton) return this.closeQuickView();

    const openSizeGuide = event.target.closest(".js-open-size-guide");
    if (openSizeGuide) return this.openSizeGuide();

    const closeSizeGuide = event.target.closest(
      ".js-close-size-guide, .js-close-size-guide + .size-guide__background-close"
    );
    if (closeSizeGuide) return this.closeSizeGuide();
  }

  openSizeGuide() {
    const sizeGuide = document.querySelector("product-size-guide .js-size-guide");
    const guideOverlay = document.querySelector("product-size-guide .size-guide__background-close");

    [sizeGuide, guideOverlay].forEach((el) => {
      if (el) {
        el.classList.add("display");
        setTimeout(() => el.classList.add("active"), 50);
      }
    });
  }

  closeSizeGuide() {
    const sizeGuide = document.querySelector("product-size-guide .js-size-guide");
    const guideOverlay = document.querySelector("product-size-guide .size-guide__background-close");

    [sizeGuide, guideOverlay].forEach((el) => el?.classList.add("closing"));

    setTimeout(() => {
      [sizeGuide, guideOverlay].forEach((el) =>
        el?.classList.remove("active", "closing", "display")
      );
    }, 300);
  }

  setLoadingIndicator(isLoading, triggerElement) {
    const button = triggerElement?.closest(".js-open-quickview");
    const span = button?.querySelector("span");
    if (!button || !span) return;

    if (isLoading) {
      if (!button.hasAttribute("data-original-text")) {
        button.setAttribute("data-original-text", span.textContent);
      }
      const loadingLabel = button.dataset.loadingText;
      if (loadingLabel) span.textContent = loadingLabel;
    } else {
      const originalLabel = button.dataset.originalText;
      if (originalLabel) span.textContent = originalLabel;
    }
  }
}

if (!customElements.get("quick-view")) {
  customElements.define("quick-view", QuickView);
}